import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Clock, Trophy, CheckCircle, BookOpen, Award, Target, PlayCircle, 
  RotateCcw, BarChart3, Server, Settings, Container, GitBranch,
  Cloud, ChevronRight, ChevronLeft, Timer, Brain, Zap, Medal,
  TrendingUp, Layers, Activity, Code
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../hooks/useCurrency';
import { useAllAccess } from '../hooks/useAllAccess';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import MockTestDashboard from '../components/MockTestDashboard';

// CDN Logo URLs (DevIcon) - Same as Courses page
const LOGO_URLS = {
  REDHAT: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg',
  LINUX: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg',
  ANSIBLE: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg',
  DOCKER: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
  TERRAFORM: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg',
  GIT: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
  PYTHON: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg',
  C_LANG: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg',
  CPP: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg',
  MYSQL: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
  JAVA: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg',
  KUBERNETES: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg',
  AWS: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg',
};

// Course configuration with icons, colors and logos
const COURSE_CONFIG = {
  RH124: {
    name: 'RH124',
    fullName: 'Linux Fundamentals',
    icon: Server,
    logo: LOGO_URLS.REDHAT,
    color: 'from-red-500 to-red-600',
    bgColor: 'bg-red-500',
    lightBg: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-600',
    description: 'RHCSA Foundation - System Administration I'
  },
  RH134: {
    name: 'RH134',
    fullName: 'Linux Administration',
    icon: Settings,
    logo: LOGO_URLS.REDHAT,
    color: 'from-red-600 to-red-700',
    bgColor: 'bg-red-600',
    lightBg: 'bg-red-50',
    borderColor: 'border-red-300',
    textColor: 'text-red-700',
    description: 'RHCSA Mastery - System Administration II'
  },
  SVRADM: {
    name: 'SVRADM',
    fullName: 'Server Administration',
    icon: Server,
    logo: LOGO_URLS.LINUX,
    color: 'from-violet-500 to-violet-600',
    bgColor: 'bg-violet-500',
    lightBg: 'bg-violet-50',
    borderColor: 'border-violet-200',
    textColor: 'text-violet-600',
    description: 'SSH, DNS, Apache, NFS, Samba, MariaDB'
  },
  ANSIBLE: {
    name: 'ANSIBLE',
    fullName: 'Automation',
    icon: Settings,
    logo: LOGO_URLS.ANSIBLE,
    color: 'from-slate-600 to-slate-700',
    bgColor: 'bg-slate-600',
    lightBg: 'bg-slate-50',
    borderColor: 'border-slate-200',
    textColor: 'text-slate-700',
    description: 'RHCE Prep - Playbooks, Roles, Vault'
  },
  DOCKER: {
    name: 'DOCKER',
    fullName: 'Containers',
    icon: Container,
    logo: LOGO_URLS.DOCKER,
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-500',
    lightBg: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-600',
    description: 'DO188 Prep - Docker, Podman, Images'
  },
  TERRAFORM: {
    name: 'TERRAFORM',
    fullName: 'Infrastructure as Code',
    icon: Cloud,
    logo: LOGO_URLS.TERRAFORM,
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-500',
    lightBg: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-600',
    description: 'IaC - Providers, Modules, State'
  },
  KUBERNETES: {
    name: 'KUBERNETES',
    fullName: 'Container Orchestration',
    icon: Container,
    logo: LOGO_URLS.KUBERNETES,
    color: 'from-blue-400 to-indigo-600',
    bgColor: 'bg-indigo-500',
    lightBg: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-indigo-600',
    description: 'k3s - Pods, Services, Ingress, Helm'
  },
  GIT: {
    name: 'GIT',
    fullName: 'Version Control',
    icon: GitBranch,
    logo: LOGO_URLS.GIT,
    color: 'from-orange-500 to-red-500',
    bgColor: 'bg-orange-500',
    lightBg: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-600',
    description: 'Git & GitHub - Branching, Merging'
  },
  PYTHON: {
    name: 'PYTHON',
    fullName: 'Python Programming',
    icon: Code,
    logo: LOGO_URLS.PYTHON,
    color: 'from-yellow-400 to-yellow-500',
    bgColor: 'bg-yellow-500',
    lightBg: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    textColor: 'text-yellow-600',
    description: 'Variables, Functions, OOP, File I/O'
  },
  C_LANG: {
    name: 'C_LANG',
    fullName: 'C Programming',
    icon: Code,
    logo: LOGO_URLS.C_LANG,
    color: 'from-blue-400 to-blue-600',
    bgColor: 'bg-blue-500',
    lightBg: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-600',
    description: 'Pointers, Arrays, Memory, File I/O'
  },
  CPP: {
    name: 'CPP',
    fullName: 'C++ Programming',
    icon: Code,
    logo: LOGO_URLS.CPP,
    color: 'from-indigo-500 to-purple-600',
    bgColor: 'bg-indigo-500',
    lightBg: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    textColor: 'text-indigo-600',
    description: 'OOP, STL, Templates, Smart Pointers'
  },
  MYSQL: {
    name: 'MYSQL',
    fullName: 'MySQL/MariaDB',
    icon: Layers,
    logo: LOGO_URLS.MYSQL,
    color: 'from-orange-500 to-amber-500',
    bgColor: 'bg-orange-500',
    lightBg: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-600',
    description: 'SQL, Joins, Indexes, Administration'
  },
  JAVA: {
    name: 'JAVA',
    fullName: 'Core Java',
    icon: Code,
    logo: LOGO_URLS.JAVA,
    color: 'from-red-500 to-orange-500',
    bgColor: 'bg-red-500',
    lightBg: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-600',
    description: 'OOP, Collections, Multithreading, JVM'
  },
  AWS_CCP: {
    name: 'AWS_CCP',
    fullName: 'AWS Cloud Practitioner',
    icon: Cloud,
    logo: LOGO_URLS.AWS,
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-500',
    lightBg: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-600',
    description: 'CLF-C02 Prep - IAM, EC2, S3, RDS, ELB, CloudWatch'
  },
  AWS_SAA: {
    name: 'AWS_SAA',
    fullName: 'AWS Solutions Architect',
    icon: Cloud,
    logo: LOGO_URLS.AWS,
    color: 'from-orange-500 to-yellow-500',
    bgColor: 'bg-orange-500',
    lightBg: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-600',
    description: 'SAA-C03 Prep - VPC, Aurora, Lambda, HA Architecture'
  },
};

const MockTests = () => {
  const { user } = useAuth();
  const { isIndia } = useCurrency();
  const { ownsMockTests } = useAllAccess();
  const [selectedTest, setSelectedTest] = useState(null);
  const [originalTestInfo, setOriginalTestInfo] = useState(null); // Issue 1 Fix: Store original test for retake
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [testStarted, setTestStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [submittingTest, setSubmittingTest] = useState(false);
  const [startingTestId, setStartingTestId] = useState(null); // Issue 4 Fix: Track which specific test is loading
  const [testCatalog, setTestCatalog] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [activeCategory, setActiveCategory] = useState('ALL');
  const [categories, setCategories] = useState([]);
  const [testHistories, setTestHistories] = useState({});
  
  // Format price based on location
  const formatPrice = (inr, usd) => isIndia ? `₹${inr}` : `$${usd}`;

  useEffect(() => {
    loadTestCatalog();
  }, [user]);

  const loadTestCatalog = async () => {
    try {
      const response = await apiService.getTestCatalog();
      setTestCatalog(response.catalog);
      setCategories(response.categories || Object.keys(response.catalog));
      
      // Issue 6 Fix: Load test histories in parallel using Promise.all
      if (user) {
        const allTests = Object.values(response.catalog).flat();
        const historyPromises = allTests.map(async (test) => {
          try {
            const history = await apiService.getTestHistory(test.id);
            return { testId: test.id, history };
          } catch (e) {
            return { testId: test.id, history: null };
          }
        });
        
        const results = await Promise.all(historyPromises);
        const histories = {};
        results.forEach(({ testId, history }) => {
          if (history && history.total_attempts > 0) {
            histories[testId] = history;
          }
        });
        setTestHistories(histories);
      }
    } catch (error) {
      console.error('Failed to load test catalog:', error);
      toast.error('Failed to load test catalog');
    } finally {
      setLoadingCatalog(false);
    }
  };

  // Timer warning state to prevent multiple alerts
  const [warningShown, setWarningShown] = useState({ five: false, one: false });
  
  // Issue 2 Fix: Use ref for auto-submit to avoid stale closure
  const autoSubmitRef = React.useRef(null);
  
  // Update ref whenever answers change
  React.useEffect(() => {
    autoSubmitRef.current = { answers, selectedTest, startedAt, timeLeft };
  }, [answers, selectedTest, startedAt, timeLeft]);

  // Timer effect - Issue 2 & 7 Fix: Proper auto-submit handling
  useEffect(() => {
    let timer;
    if (testStarted && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Issue 7: Show final warning before auto-submit
            toast.error('⏱️ Time is up! Submitting your test...', { duration: 3000 });
            // Use setTimeout to allow toast to show before submit
            setTimeout(() => {
              if (autoSubmitRef.current) {
                handleAutoSubmit();
              }
            }, 500);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [testStarted]); // Removed timeLeft from deps to prevent re-creating interval

  // Timer warnings at 5 min and 1 min
  useEffect(() => {
    if (!testStarted || !timeLeft) return;
    
    // 5 minute warning
    if (timeLeft === 300 && !warningShown.five) {
      toast.warning('⏰ Only 5 minutes remaining!', { duration: 5000 });
      setWarningShown(prev => ({ ...prev, five: true }));
    }
    
    // 1 minute warning  
    if (timeLeft === 60 && !warningShown.one) {
      toast.error('🚨 Only 1 minute left! Submit now!', { duration: 10000 });
      setWarningShown(prev => ({ ...prev, one: true }));
    }
    
    // 30 seconds warning
    if (timeLeft === 30) {
      toast.error('⚠️ 30 seconds! Auto-submitting soon...', { duration: 5000 });
    }
  }, [timeLeft, testStarted, warningShown]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Issue 1 & 4 Fix: Added loading state and store original test info
  const startTest = async (test) => {
    if (startingTestId) return; // Prevent double-click
    
    // Check if test is paid and user doesn't own mock tests — redirect to payment
    if (!test.is_free && !ownsMockTests) {
      window.location.href = '/payment/MOCK_TESTS';
      return;
    }
    
    setStartingTestId(test.id); // Track specific test being started
    try {
      const questionsResponse = await apiService.getTestQuestions(test.id);
      const testWithQuestions = {
        ...questionsResponse.test,
        category: test.category // Preserve category from catalog
      };

      const startResponse = await apiService.startMockTest({
        test_id: test.id,
        test_title: test.title,
        test_level: test.level,
        questions_count: testWithQuestions.questions.length,
        duration_minutes: test.duration,
        passing_score: test.passing_score
      });

      setOriginalTestInfo(test); // Issue 1: Store original test for retake
      setSelectedTest(testWithQuestions);
      setCurrentQuestion(0);
      setAnswers({});
      setShowResults(false);
      setTestStarted(true);
      setTimeLeft(test.duration * 60);
      setAttemptId(startResponse.attempt_id);
      setStartedAt(startResponse.started_at);
      setWarningShown({ five: false, one: false }); // Reset warnings for new test
      
      toast.success(`Test Started! Good luck with ${test.title}!`);
    } catch (error) {
      console.error('Failed to start test:', error);
      // If 403 error (not purchased), redirect to payment
      if (error.response?.status === 403) {
        toast.info('Please purchase mock tests to access this test');
        window.location.href = '/payment/MOCK_TESTS';
      } else {
        toast.error('Failed to start test. Please try again.');
      }
    } finally {
      setStartingTestId(null);
    }
  };

  const handleAnswer = (questionId, answerIndex) => {
    // Issue 3 Fix: Ensure question ID is string for consistency with backend
    setAnswers(prev => ({ ...prev, [String(questionId)]: answerIndex }));
  };

  // Issue 2 Fix: Separate function for auto-submit using ref
  const handleAutoSubmit = async () => {
    if (submittingTest) return;
    
    const current = autoSubmitRef.current;
    if (!current || !current.selectedTest) return;
    
    setSubmittingTest(true);
    try {
      const timeSpent = (current.selectedTest.duration * 60);
      
      const result = await apiService.submitMockTest({
        test_id: current.selectedTest.id,
        test_title: current.selectedTest.title,
        test_level: current.selectedTest.level,
        category: current.selectedTest.category,
        answers: current.answers,
        time_spent: timeSpent,
        started_at: current.startedAt
      });

      setShowResults({
        correct: result.correct_answers,
        total: result.total_questions,
        percentage: result.score_percentage,
        passed: result.passed,
        passingScore: result.passing_score,
        timeSpent: result.time_spent,
        attemptId: result.attempt_id,
        questionsResults: result.questions_results || []
      });

      setTestStarted(false);
      
      if (result.passed) {
        toast.success(`Congratulations! You passed with ${result.score_percentage}%!`);
      } else {
        toast.error(`You scored ${result.score_percentage}%. Keep practicing!`);
      }
    } catch (error) {
      console.error('Failed to submit test:', error);
      toast.error('Failed to submit test. Please try again.');
    } finally {
      setSubmittingTest(false);
    }
  };

  const handleSubmitTest = async () => {
    if (submittingTest) return;
    
    setSubmittingTest(true);
    try {
      const timeSpent = (selectedTest.duration * 60) - timeLeft;
      
      const result = await apiService.submitMockTest({
        test_id: selectedTest.id,
        test_title: selectedTest.title,
        test_level: selectedTest.level,
        category: selectedTest.category,
        answers: answers,
        time_spent: timeSpent,
        started_at: startedAt
      });

      setShowResults({
        correct: result.correct_answers,
        total: result.total_questions,
        percentage: result.score_percentage,
        passed: result.passed,
        passingScore: result.passing_score,
        timeSpent: result.time_spent,
        attemptId: result.attempt_id,
        questionsResults: result.questions_results || []  // Per-question results from backend
      });

      setTestStarted(false);
      
      if (result.passed) {
        toast.success(`Congratulations! You passed with ${result.score_percentage}%!`);
      } else {
        toast.error(`You scored ${result.score_percentage}%. Keep practicing!`);
      }
    } catch (error) {
      console.error('Failed to submit test:', error);
      toast.error('Failed to submit test. Please try again.');
    } finally {
      setSubmittingTest(false);
    }
  };

  // Issue 10 Fix: Reset test and refresh histories
  const resetTest = async () => {
    setSelectedTest(null);
    setOriginalTestInfo(null);
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setTestStarted(false);
    setTimeLeft(null);
    setAttemptId(null);
    setStartedAt(null);
    setSubmittingTest(false);
    setStartingTestId(null);
    setWarningShown({ five: false, one: false });
    
    // Refresh test histories after completing a test
    if (user && testCatalog) {
      try {
        const allTests = Object.values(testCatalog).flat();
        const historyPromises = allTests.map(async (test) => {
          try {
            const history = await apiService.getTestHistory(test.id);
            return { testId: test.id, history };
          } catch (e) {
            return { testId: test.id, history: null };
          }
        });
        
        const results = await Promise.all(historyPromises);
        const histories = {};
        results.forEach(({ testId, history }) => {
          if (history && history.total_attempts > 0) {
            histories[testId] = history;
          }
        });
        setTestHistories(histories);
      } catch (e) {
        // Silently fail - not critical
      }
    }
  };

  const getLevelBadgeColor = (level) => {
    switch (level) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-amber-100 text-amber-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get total stats
  const getTotalStats = () => {
    if (!testCatalog) return { tests: 0, questions: 0 };
    let tests = 0, questions = 0;
    Object.values(testCatalog).forEach(categoryTests => {
      tests += categoryTests.length;
      categoryTests.forEach(t => questions += t.total_questions);
    });
    return { tests, questions };
  };

  // Filter tests by category
  const getFilteredCatalog = () => {
    if (!testCatalog) return {};
    if (activeCategory === 'ALL') return testCatalog;
    return { [activeCategory]: testCatalog[activeCategory] || [] };
  };

  // Results Screen
  if (showResults) {
    const courseConfig = COURSE_CONFIG[selectedTest.category] || COURSE_CONFIG.RH124;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Results Header */}
          <div className="text-center mb-8">
            <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full mb-4 ${
              showResults.passed ? 'bg-gradient-to-br from-green-400 to-emerald-500' : 'bg-gradient-to-br from-red-400 to-red-500'
            } shadow-lg`}>
              {showResults.passed ? (
                <Trophy className="h-12 w-12 text-white" />
              ) : (
                <RotateCcw className="h-12 w-12 text-white" />
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
              {showResults.passed ? 'Excellent Work!' : 'Keep Practicing!'}
            </h1>
            <p className="text-lg text-gray-600">
              {selectedTest.title}
            </p>
            <Badge className={`mt-2 ${courseConfig.bgColor} text-white`}>
              {selectedTest.category}
            </Badge>
          </div>

          {/* Score Card */}
          <Card className="mb-8 shadow-xl border-0 overflow-hidden">
            <div className={`h-2 bg-gradient-to-r ${courseConfig.color}`}></div>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-4xl font-bold text-gray-900 mb-1">{showResults.percentage}%</div>
                  <p className="text-gray-600 text-sm">Your Score</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-4xl font-bold text-green-600 mb-1">{showResults.correct}</div>
                  <p className="text-gray-600 text-sm">Correct</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-4xl font-bold text-red-600 mb-1">{showResults.total - showResults.correct}</div>
                  <p className="text-gray-600 text-sm">Incorrect</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className={`text-4xl font-bold mb-1 ${showResults.passed ? 'text-green-600' : 'text-red-600'}`}>
                    {showResults.passed ? 'PASS' : 'FAIL'}
                  </div>
                  <p className="text-gray-600 text-sm">Required: {showResults.passingScore}%</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-8">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Score Progress</span>
                  <span>{showResults.percentage}% / {showResults.passingScore}% required</span>
                </div>
                <div className="relative w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className={`absolute top-0 left-0 h-4 rounded-full transition-all duration-1000 ${
                      showResults.passed ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-red-500 to-red-600'
                    }`}
                    style={{ width: `${showResults.percentage}%` }}
                  ></div>
                  <div 
                    className="absolute top-0 h-4 w-0.5 bg-gray-800"
                    style={{ left: `${showResults.passingScore}%` }}
                  ></div>
                </div>
              </div>

              {/* Actions - Issue 1 Fix: Use originalTestInfo for retake */}
              <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
                <Button
                  onClick={() => originalTestInfo && startTest(originalTestInfo)}
                  disabled={startingTestId || !originalTestInfo}
                  className={`bg-gradient-to-r ${courseConfig.color} hover:opacity-90 text-white px-6`}
                >
                  {startingTestId ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Starting...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Retake Test
                    </>
                  )}
                </Button>
                <Button onClick={resetTest} variant="outline" className="px-6">
                  Back to All Tests
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Answer Review - Uses backend results for security */}
          <Card className="shadow-xl">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                Answer Review
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {showResults.questionsResults && showResults.questionsResults.length > 0 ? (
                  showResults.questionsResults.map((qResult, index) => {
                    // Issue 5 Fix: Handle unanswered questions
                    const isUnanswered = qResult.user_answer === null || qResult.user_answer === undefined;
                    
                    return (
                      <div key={qResult.id} className={`p-4 rounded-xl border-2 ${
                        qResult.is_correct ? 'bg-green-50 border-green-200' 
                          : isUnanswered ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            qResult.is_correct ? 'bg-green-500 text-white' 
                              : isUnanswered ? 'bg-yellow-500 text-white'
                              : 'bg-red-500 text-white'
                          }`}>
                            {qResult.is_correct ? '✓' : isUnanswered ? '?' : '✗'}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-3">
                              <h4 className="font-semibold text-gray-900">
                                Q{index + 1}: {qResult.question}
                              </h4>
                              {isUnanswered && (
                                <Badge className="bg-yellow-100 text-yellow-800 text-xs">Not Answered</Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-1 gap-2 mb-3">
                              {qResult.options.map((option, optionIndex) => (
                                <div key={optionIndex} className={`p-2 rounded-lg text-sm ${
                                  optionIndex === qResult.correct 
                                    ? 'bg-green-100 text-green-800 font-semibold border border-green-300' 
                                    : optionIndex === qResult.user_answer && !qResult.is_correct
                                    ? 'bg-red-100 text-red-800 border border-red-300'
                                    : 'bg-white text-gray-600 border border-gray-200'
                                }`}>
                                  <span className="font-medium mr-2">{String.fromCharCode(65 + optionIndex)}.</span>
                                  {option}
                                  {optionIndex === qResult.correct && <span className="ml-2 text-green-600">✓ Correct</span>}
                                  {optionIndex === qResult.user_answer && !qResult.is_correct && <span className="ml-2 text-red-600">✗ Your answer</span>}
                                </div>
                              ))}
                            </div>
                            {qResult.explanation && (
                              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800 border border-blue-200">
                                <strong>Explanation:</strong> {qResult.explanation}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Answer review not available for this attempt.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Test Taking Screen
  if (selectedTest && testStarted) {
    const currentQ = selectedTest.questions[currentQuestion];
    const courseConfig = COURSE_CONFIG[selectedTest.category] || COURSE_CONFIG.RH124;
    const answeredCount = Object.keys(answers).length;
    
    // Exit test confirmation handler
    const handleExitTest = () => {
      if (window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
        resetTest();
      }
    };
    
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Exit Button */}
          <div className="mb-4">
            <Button 
              onClick={handleExitTest}
              variant="ghost" 
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Exit Test
            </Button>
          </div>
          
          {/* Test Header */}
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`${courseConfig.bgColor} text-white`}>
                    {selectedTest.category}
                  </Badge>
                  <Badge className={getLevelBadgeColor(selectedTest.level)}>
                    {selectedTest.level}
                  </Badge>
                </div>
                <h1 className="text-xl font-bold text-gray-900">{selectedTest.title}</h1>
                <p className="text-gray-600 text-sm">
                  Question {currentQuestion + 1} of {selectedTest.questions.length} | 
                  Answered: {answeredCount}/{selectedTest.questions.length}
                </p>
              </div>
              
              {/* Timer */}
              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${
                timeLeft < 300 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
              }`}>
                <Timer className="h-5 w-5" />
                <span className="font-mono text-xl font-bold">{formatTime(timeLeft)}</span>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex gap-1">
                {selectedTest.questions.map((_, idx) => (
                  <div 
                    key={idx}
                    className={`flex-1 h-2 rounded-full transition-all ${
                      idx === currentQuestion 
                        ? `bg-gradient-to-r ${courseConfig.color}` 
                        : answers[String(selectedTest.questions[idx].id)] !== undefined
                        ? 'bg-green-400'
                        : 'bg-gray-200'
                    }`}
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* Question Card */}
          <Card className="shadow-xl border-0">
            <div className={`h-1 bg-gradient-to-r ${courseConfig.color}`}></div>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg sm:text-xl text-gray-900">
                {currentQ.question}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="space-y-3">
                {currentQ.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(currentQ.id, index)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                      answers[String(currentQ.id)] === index
                        ? `bg-gradient-to-r ${courseConfig.color} text-white border-transparent`
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${
                        answers[String(currentQ.id)] === index
                          ? 'bg-white/20 border-white/40 text-white'
                          : 'border-gray-300 text-gray-500'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </div>
                      <span className="flex-1">{option}</span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t">
                <Button
                  onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                  disabled={currentQuestion === 0}
                  variant="outline"
                  className="gap-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                <div className="flex gap-2">
                  {currentQuestion === selectedTest.questions.length - 1 ? (
                    <Button
                      onClick={() => {
                        // Issue 9 Fix: Confirm before submit
                        const unanswered = selectedTest.questions.length - Object.keys(answers).length;
                        let confirmMsg = 'Are you sure you want to submit the test?';
                        if (unanswered > 0) {
                          confirmMsg = `You have ${unanswered} unanswered question(s). Are you sure you want to submit?`;
                        }
                        if (window.confirm(confirmMsg)) {
                          handleSubmitTest();
                        }
                      }}
                      disabled={submittingTest}
                      className="bg-green-600 hover:bg-green-700 text-white gap-2"
                    >
                      {submittingTest ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4" />
                          Submit Test
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setCurrentQuestion(currentQuestion + 1)}
                      className={`bg-gradient-to-r ${courseConfig.color} hover:opacity-90 text-white gap-2`}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Question Navigator */}
          <div className="mt-6 bg-white rounded-xl shadow-lg p-4">
            <p className="text-sm text-gray-600 mb-3 font-medium">Jump to Question:</p>
            <div className="flex flex-wrap gap-2">
              {selectedTest.questions.map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentQuestion(idx)}
                  className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                    idx === currentQuestion
                      ? `bg-gradient-to-r ${courseConfig.color} text-white`
                      : answers[String(q.id)] !== undefined
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard View
  if (showDashboard) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-4 shadow-lg">
              <BarChart3 className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Progress Dashboard</h1>
            <p className="text-gray-600 mb-4">Track your performance across all DevOps mock tests</p>
            <Button onClick={() => setShowDashboard(false)} variant="outline">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Tests
            </Button>
          </div>
          <MockTestDashboard onSelectTest={() => setShowDashboard(false)} />
        </div>
      </div>
    );
  }

  // Main Test Selection Screen
  const stats = getTotalStats();
  const filteredCatalog = getFilteredCatalog();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mock-tests-page">
      {/* Hero Section - Interview Prep Focused */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Left: Icon + Title */}
            <div className="flex items-center gap-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl shadow-lg flex-shrink-0">
                <Brain className="h-6 w-6 text-white" />
              </div>
              <div className="text-center sm:text-left">
                <h1 className="text-2xl sm:text-3xl font-bold">Interview Prep Mock Tests</h1>
                <p className="text-sm text-slate-400 hidden sm:block">Crack Linux, DevOps & Programming Interviews</p>
              </div>
            </div>
            
            {/* Center: Stats */}
            <div className="flex items-center gap-4">
              <div className="text-center px-3">
                <div className="text-xl font-bold text-amber-400">{categories.length}</div>
                <div className="text-xs text-slate-400">Courses</div>
              </div>
              <div className="text-center px-3 border-l border-slate-700">
                <div className="text-xl font-bold text-green-400">{stats.tests}</div>
                <div className="text-xs text-slate-400">Tests</div>
              </div>
              <div className="text-center px-3 border-l border-slate-700">
                <div className="text-xl font-bold text-blue-400">{stats.questions}+</div>
                <div className="text-xs text-slate-400">Questions</div>
              </div>
            </div>

            {/* Right: Buttons */}
            <div className="flex items-center gap-2">
              {ownsMockTests ? (
                <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white px-3 py-1.5" data-testid="mock-tests-owned-badge">
                  <CheckCircle className="h-3.5 w-3.5 mr-1" />
                  Mock Tests Unlocked
                </Badge>
              ) : (
                <Button 
                  onClick={() => window.location.href = '/payment/MOCK_TESTS'}
                  size="sm"
                  data-testid="unlock-mock-tests-btn"
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                >
                  <Award className="h-4 w-4 mr-2" />
                  Unlock All @ {formatPrice(499, 19)}
                </Button>
              )}
              {user && (
                <Button 
                  onClick={() => setShowDashboard(true)}
                  size="sm"
                  variant="outline"
                  className="border-slate-600 text-white hover:bg-slate-700"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  My Progress
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Category Tabs with Scroll Arrows */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="relative flex items-center py-3">
            {/* Left Arrow */}
            <button
              onClick={() => {
                const el = document.getElementById('mock-tabs-scroll');
                if (el) el.scrollBy({ left: -200, behavior: 'smooth' });
              }}
              className="absolute left-0 z-20 w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-md hover:bg-gray-50 transition-all flex-shrink-0"
              style={{ marginLeft: '-2px' }}
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-4 w-4 text-gray-600" />
            </button>

            {/* Scrollable Tabs */}
            <div className="relative mx-8 overflow-hidden flex-1">
              {/* Left fade */}
              <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white to-transparent z-10" />
              {/* Right fade */}
              <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white to-transparent z-10" />
              
              <div 
                id="mock-tabs-scroll"
                className="flex items-center gap-2 overflow-x-auto hide-scrollbar py-1 px-1"
              >
                <button
                  onClick={() => setActiveCategory('ALL')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                    activeCategory === 'ALL'
                      ? 'bg-slate-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Layers className="h-4 w-4 inline mr-2" />
                  All Tests
                </button>
                {categories.map(cat => {
                  const config = COURSE_CONFIG[cat];
                  if (!config) return null;
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                        activeCategory === cat
                          ? `bg-gradient-to-r ${config.color} text-white`
                          : `${config.lightBg} ${config.textColor} hover:opacity-80`
                      }`}
                    >
                      {config.logo ? (
                        <img src={config.logo} alt={cat} className="h-5 w-5 rounded object-cover" />
                      ) : (
                        <config.icon className="h-4 w-4" />
                      )}
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => {
                const el = document.getElementById('mock-tabs-scroll');
                if (el) el.scrollBy({ left: 200, behavior: 'smooth' });
              }}
              className="absolute right-0 z-20 w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-md hover:bg-gray-50 transition-all flex-shrink-0"
              style={{ marginRight: '-2px' }}
              aria-label="Scroll right"
            >
              <ChevronRight className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Test Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loadingCatalog ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading tests...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-12">
            {Object.entries(filteredCatalog).map(([category, tests]) => {
              if (!tests || tests.length === 0) return null;
              const config = COURSE_CONFIG[category];
              if (!config) return null;
              const Icon = config.icon;

              return (
                <div key={category}>
                  {/* Category Header */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center shadow-lg overflow-hidden`}>
                      {config.logo ? (
                        <img src={config.logo} alt={category} className="w-full h-full object-cover" />
                      ) : (
                        <Icon className="h-6 w-6 text-white" />
                      )}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {category} - {config.fullName}
                      </h2>
                      <p className="text-gray-600">{config.description}</p>
                    </div>
                    <div className="ml-auto">
                      <Badge variant="outline" className="text-sm">
                        {tests.length} {tests.length === 1 ? 'Test' : 'Tests'}
                      </Badge>
                    </div>
                  </div>

                  {/* Bundle pricing note — shown only when user hasn't purchased yet */}
                  {!ownsMockTests && tests.some((t) => !t.is_free) && (
                    <div className="mb-5 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                      <span className="text-lg">🔓</span>
                      <p className="text-sm text-amber-800">
                        <strong>Full Access Bundle — {formatPrice(499, 19)}</strong> &nbsp;·&nbsp; Unlock <em>all</em> premium tests across every category with a single purchase.
                      </p>
                      <button
                        onClick={() => window.location.href = '/payment/MOCK_TESTS'}
                        className="ml-auto flex-shrink-0 text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Unlock All
                      </button>
                    </div>
                  )}

                  {/* Test Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tests.map((test) => (
                      <Card 
                        key={test.id} 
                        className={`group hover:shadow-xl transition-all duration-300 border-2 hover:${config.borderColor} overflow-hidden`}
                      >
                        <div className={`h-1.5 bg-gradient-to-r ${config.color}`}></div>
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {config.logo && (
                                <img src={config.logo} alt={category} className="h-6 w-6 rounded object-cover" />
                              )}
                              <Badge className={getLevelBadgeColor(test.level)}>
                                {test.level}
                              </Badge>
                            </div>
                            <div className="flex items-center text-gray-500 text-sm">
                              <Clock className="h-4 w-4 mr-1" />
                              {test.duration} min
                            </div>
                          </div>
                          <CardTitle className="text-lg group-hover:text-gray-900">
                            {test.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          {/* Previous Attempt Info */}
                          {testHistories[test.id] && (
                            <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-blue-700 flex items-center gap-1">
                                  <RotateCcw className="h-3 w-3" />
                                  Previous Attempts: {testHistories[test.id].total_attempts}
                                </span>
                                {testHistories[test.id].passed_count > 0 && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    Passed {testHistories[test.id].passed_count}x
                                  </Badge>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="flex items-center gap-1">
                                  <Trophy className="h-3 w-3 text-yellow-500" />
                                  <span className="text-gray-600">Best:</span>
                                  <span className="font-bold text-yellow-600">{testHistories[test.id].best_score}%</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Activity className="h-3 w-3 text-blue-500" />
                                  <span className="text-gray-600">Last:</span>
                                  <span className="font-bold text-blue-600">{testHistories[test.id].last_score}%</span>
                                </div>
                              </div>
                              {testHistories[test.id].improvement !== 0 && (
                                <div className={`mt-2 text-xs flex items-center gap-1 ${testHistories[test.id].improvement > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  <TrendingUp className={`h-3 w-3 ${testHistories[test.id].improvement < 0 ? 'rotate-180' : ''}`} />
                                  {testHistories[test.id].improvement > 0 ? '+' : ''}{testHistories[test.id].improvement}% from first attempt
                                </div>
                              )}
                            </div>
                          )}

                          {/* Stats */}
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className={`text-center p-3 ${config.lightBg} rounded-xl`}>
                              <div className={`text-2xl font-bold ${config.textColor}`}>
                                {test.total_questions}
                              </div>
                              <div className="text-xs text-gray-600">Questions</div>
                            </div>
                            <div className="text-center p-3 bg-green-50 rounded-xl">
                              <div className="text-2xl font-bold text-green-600">
                                {test.passing_score}%
                              </div>
                              <div className="text-xs text-gray-600">Pass Score</div>
                            </div>
                          </div>

                          {/* Topics */}
                          <div className="mb-4">
                            <p className="text-xs font-medium text-gray-700 mb-2">Topics:</p>
                            <div className="flex flex-wrap gap-1">
                              {test.topics.slice(0, 4).map((topic, idx) => (
                                <span 
                                  key={idx} 
                                  className="px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-600"
                                >
                                  {topic}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Free/Paid Badge */}
                          {test.is_free ? (
                            <div className="mb-3 flex items-center justify-center">
                              <Badge className="bg-green-100 text-green-700 px-3 py-1">
                                <Zap className="h-3 w-3 mr-1" />
                                FREE
                              </Badge>
                            </div>
                          ) : ownsMockTests ? (
                            <div className="mb-3 flex items-center justify-center">
                              <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Unlocked
                              </Badge>
                            </div>
                          ) : (
                            <div className="mb-3 flex items-center justify-center">
                              <Badge className="bg-amber-100 text-amber-700 px-3 py-1">
                                🔒 Premium
                              </Badge>
                            </div>
                          )}

                          {/* Start Button - Issue 4 Fix: Loading state for specific test */}
                          <Button 
                            onClick={() => startTest(test)}
                            disabled={startingTestId !== null}
                            className={`w-full bg-gradient-to-r ${config.color} hover:opacity-90 text-white font-medium py-5`}
                          >
                            {startingTestId === test.id ? (
                              <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                Loading...
                              </>
                            ) : (
                              <>
                                <PlayCircle className="h-5 w-5 mr-2" />
                                {test.is_free
                                  ? 'Start Free Test'
                                  : ownsMockTests
                                    ? (testHistories[test.id] ? 'Retake Test' : 'Start Test')
                                    : 'Unlock Full Access'}
                              </>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Custom Scrollbar Hide */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default MockTests;
